<script setup lang="ts">
import { Check } from 'lucide-vue-next'

interface Stage { code: string; title: string }

const props = withDefaults(defineProps<{
  stages: readonly Stage[]
  currentIndex: number
  orientation?: 'vertical' | 'horizontal'
  clickableCurrent?: boolean
}>(), { orientation: 'vertical', clickableCurrent: false })

const emit = defineEmits<{ select: [code: string] }>()

function stateOf(index: number): 'done' | 'current' | 'pending' {
  if (index < props.currentIndex) return 'done'
  if (index === props.currentIndex) return 'current'
  return 'pending'
}
function onSelect(index: number, code: string) {
  if (stateOf(index) === 'current' && props.clickableCurrent) emit('select', code)
}
</script>

<template>
  <ol class="ck-stepper" :class="`ck-stepper--${orientation}`">
    <li v-for="(stage, index) in stages" :key="stage.code" class="ck-stepper-item" :class="`is-${stateOf(index)}`">
      <button
        type="button"
        class="ck-stepper-node"
        :disabled="!(stateOf(index) === 'current' && clickableCurrent)"
        :aria-current="stateOf(index) === 'current' ? 'step' : undefined"
        @click="onSelect(index, stage.code)"
      >
        <span class="ck-stepper-dot"><Check v-if="stateOf(index) === 'done'" :size="14" /><template v-else>{{ index + 1 }}</template></span>
        <span class="ck-stepper-label">{{ stage.title }}</span>
      </button>
      <span v-if="index < stages.length - 1" class="ck-stepper-line" aria-hidden="true" />
    </li>
  </ol>
</template>

<style scoped>
.ck-stepper { display: flex; margin: 0; padding: 0; list-style: none; }
.ck-stepper-node { display: flex; align-items: center; gap: var(--space-3); border: 0; background: none; padding: 0; font: inherit; color: inherit; cursor: default; text-align: left; }
.ck-stepper-node:disabled { cursor: default; }
.ck-stepper-dot { display: flex; align-items: center; justify-content: center; flex-shrink: 0; width: 28px; height: 28px; border-radius: var(--radius-full); font-family: var(--font-mono); font-size: 12px; font-weight: var(--fw-bold); background: var(--ck-cream-200); color: var(--ck-text-faint); border: 1.5px solid var(--ck-cream-400); }
.is-done .ck-stepper-dot { background: var(--ck-ok-500); border-color: var(--ck-ok-500); color: white; }
.is-current .ck-stepper-dot { background: var(--ck-flame); border-color: transparent; color: white; box-shadow: var(--shadow-flame); }
.ck-stepper-label { font-size: var(--fs-sm); color: var(--ck-text-faint); }
.is-done .ck-stepper-label { color: var(--ck-text-muted); }
.is-current .ck-stepper-label { color: var(--ck-text); font-weight: var(--fw-bold); }

/* vertical: coluna, linha conectora à esquerda dos dots */
.ck-stepper--vertical { flex-direction: column; }
.ck-stepper--vertical .ck-stepper-item { position: relative; display: flex; flex-direction: column; }
.ck-stepper--vertical .ck-stepper-node[disabled] { cursor: default; }
.ck-stepper--vertical .ck-stepper-node:not([disabled]) { cursor: pointer; }
.ck-stepper--vertical .ck-stepper-line { position: absolute; left: 13.5px; top: 28px; width: 2px; height: calc(100% - 28px + var(--space-3)); background: var(--ck-cream-300); }
.ck-stepper--vertical .is-done + .ck-stepper-line, .ck-stepper--vertical .ck-stepper-item.is-done .ck-stepper-line { background: var(--ck-ok-500); }
.ck-stepper--vertical .ck-stepper-item:not(:last-child) { padding-bottom: var(--space-4); }

/* horizontal: dots em linha com trilho abaixo, rótulo abaixo do dot */
.ck-stepper--horizontal { justify-content: space-between; align-items: flex-start; position: relative; }
.ck-stepper--horizontal .ck-stepper-item { display: flex; flex-direction: column; align-items: center; flex: 1; position: relative; }
.ck-stepper--horizontal .ck-stepper-node { flex-direction: column; gap: var(--space-2); }
.ck-stepper--horizontal .ck-stepper-label { text-align: center; font-size: 11px; line-height: 1.2; }
.ck-stepper--horizontal .ck-stepper-line { position: absolute; top: 14px; left: calc(50% + 20px); width: calc(100% - 40px); height: 2px; background: var(--ck-cream-300); }
.ck-stepper--horizontal .ck-stepper-item.is-done .ck-stepper-line { background: var(--ck-flame); }
</style>
